import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, MapPin, Phone, Video, Building2, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { toast } from 'sonner';
import { getManilaTodayStr } from '../lib/dateUtils';

const ScheduleInterviewModal = ({
  isOpen,
  onClose,
  applicant,
  job,
  applicationId,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    interview_type: 'in_person',
    date: '',
    time: '',
    duration_minutes: 30,
    location: '',
    meeting_link: '',
    phone_number: '',
    notes: ''
  });

  // Get today's date in Manila timezone as minimum (allow same-day scheduling)
  const minDate = getManilaTodayStr();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required props
    if (!job?.id) {
      toast.error('Job information is missing. Please try again.');
      console.error('ScheduleInterviewModal: job prop is undefined or missing id', { job, applicant });
      return;
    }
    if (!applicant?.id) {
      toast.error('Applicant information is missing. Please try again.');
      console.error('ScheduleInterviewModal: applicant prop is undefined or missing id', { job, applicant });
      return;
    }

    if (!formData.date || !formData.time) {
      toast.error('Please select date and time');
      return;
    }

    // Validate based on interview type
    if (formData.interview_type === 'in_person' && !formData.location) {
      toast.error('Please enter the interview location');
      return;
    }
    if (formData.interview_type === 'video' && !formData.meeting_link) {
      toast.error('Please enter the video meeting link');
      return;
    }
    if (formData.interview_type === 'phone' && !formData.phone_number) {
      toast.error('Please enter the phone number');
      return;
    }

    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        return;
      }

      // Combine date and time into ISO string (Philippine timezone)
      const scheduledAt = new Date(`${formData.date}T${formData.time}:00+08:00`).toISOString();

      const response = await api.post('/api/interviews', {
        job_id: job.id,
        applicant_id: applicant.id,
        application_id: applicationId,
        interview_type: formData.interview_type,
        scheduled_at: scheduledAt,
        duration_minutes: formData.duration_minutes,
        location: formData.location || null,
        meeting_link: formData.meeting_link || null,
        phone_number: formData.phone_number || null,
        notes: formData.notes || null
      }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (response.data.success) {
        toast.success(`Interview scheduled with ${applicant.name}!`);
        onSuccess?.(response.data.interview);
        onClose();
        // Reset form
        setFormData({
          interview_type: 'in_person',
          date: '',
          time: '',
          duration_minutes: 30,
          location: '',
          meeting_link: '',
          phone_number: '',
          notes: ''
        });
      } else {
        toast.error(response.data.detail || 'Failed to schedule interview');
      }
    } catch (error) {
      console.error('Error scheduling interview:', error);
      toast.error(error.response?.data?.detail || 'Failed to schedule interview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            {t('interview.scheduleInterview')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Applicant & Job Info */}
          <div className="bg-muted/50 p-3 rounded-lg">
            <p className="font-medium">{applicant?.name}</p>
            <p className="text-sm text-muted-foreground">for {job?.title}</p>
          </div>

          {/* Interview Type */}
          <div className="space-y-2">
            <Label>{t('interview.interviewType')}</Label>
            <Select
              value={formData.interview_type}
              onValueChange={(value) => setFormData({ ...formData, interview_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in_person">
                  <div className="flex items-center gap-2">
                    <Building2 size={16} />
                    {t('interview.inPerson')}
                  </div>
                </SelectItem>
                <SelectItem value="phone">
                  <div className="flex items-center gap-2">
                    <Phone size={16} />
                    {t('interview.phone')}
                  </div>
                </SelectItem>
                <SelectItem value="video">
                  <div className="flex items-center gap-2">
                    <Video size={16} />
                    {t('interview.video')}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">{t('interview.date')}</Label>
              <Input
                id="date"
                type="date"
                min={minDate}
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="time">{t('interview.time')}</Label>
              <Input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label>{t('interview.duration')}</Label>
            <Select
              value={String(formData.duration_minutes)}
              onValueChange={(value) => setFormData({ ...formData, duration_minutes: parseInt(value) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Location (for in-person) */}
          {formData.interview_type === 'in_person' && (
            <div className="space-y-2">
              <Label htmlFor="location">
                <MapPin size={14} className="inline mr-1" />
                {t('interview.location')}
              </Label>
              <Input
                id="location"
                placeholder="e.g., 123 Main St, Tagbilaran City"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                required
              />
            </div>
          )}

          {/* Meeting Link (for video) */}
          {formData.interview_type === 'video' && (
            <div className="space-y-2">
              <Label htmlFor="meeting_link">
                <Video size={14} className="inline mr-1" />
                {t('interview.meetingLink')}
              </Label>
              <Input
                id="meeting_link"
                type="url"
                placeholder="e.g., https://zoom.us/j/123456789"
                value={formData.meeting_link}
                onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground">Zoom, Google Meet, Messenger, etc.</p>
            </div>
          )}

          {/* Phone Number (for phone) */}
          {formData.interview_type === 'phone' && (
            <div className="space-y-2">
              <Label htmlFor="phone_number">
                <Phone size={14} className="inline mr-1" />
                {t('interview.phoneNumber')}
              </Label>
              <Input
                id="phone_number"
                type="tel"
                placeholder="e.g., 09171234567"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                required
              />
              <p className="text-xs text-muted-foreground">The number you'll call or be called at</p>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('interview.additionalNotes')}</Label>
            <Textarea
              id="notes"
              placeholder="e.g., Please bring your resume and valid ID"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />
                  {t('interview.scheduling')}
                </>
              ) : (
                <>
                  <Calendar size={16} className="mr-2" />
                  {t('interview.sendInvite')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleInterviewModal;
